import React from 'react';
import { Campaign } from '../../types/Campaign';
import { CalendarIcon, DollarSignIcon, TagIcon, PercentIcon, ArrowRightIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="overflow-x-auto" data-id="element-532">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-533">
        <thead className="bg-gray-50" data-id="element-534">
          <tr data-id="element-535">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-536">
              Campaign
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-537">
              Code
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-538">
              Discount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-539">
              Budget Usage
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-540">
              Expiry
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-541">
              Status
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-542">
              <span className="sr-only" data-id="element-543">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-544">
          {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onCampaignClick(campaign.id)} data-id="element-545">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-546">
                  <div className="flex items-center" data-id="element-547">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-548">
                      <TagIcon size={18} className="text-blue-600" data-id="element-549" />
                    </div>
                    <div className="ml-4" data-id="element-550">
                      <div className="text-sm font-medium text-gray-900" data-id="element-551">
                        {campaign.title}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-552">
                        {campaign.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-553">
                  <div className="text-sm font-medium text-gray-900" data-id="element-554">
                    {campaign.code}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-555">
                    {campaign.codeStrategy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-556">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-557">
                    {campaign.discountPercentage > 0 ? <>
                        <PercentIcon size={16} className="mr-1 text-purple-500" data-id="element-558" />
                        {campaign.discountPercentage}%
                      </> : <>
                        <DollarSignIcon size={16} className="mr-1 text-green-500" data-id="element-559" />
                        {campaign.fixedDiscountAmount} {campaign.currency}
                      </>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-560">
                    {campaign.discountCarriedBy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-561">
                  <div className="flex items-center" data-id="element-562">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-563">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${calculateBudgetUsage(campaign)}%`
                }} data-id="element-564"></div>
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-565">
                      {calculateBudgetUsage(campaign)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-566">
                    {campaign.budgetUsed.toLocaleString()} /{' '}
                    {campaign.budget.toLocaleString()} {campaign.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-567">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-568">
                    <CalendarIcon size={16} className="mr-1 text-gray-500" data-id="element-569" />
                    {campaign.expiryDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-570">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`} data-id="element-571">
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-572">
                  <button className="text-blue-600 hover:text-blue-900" data-id="element-573">
                    <ArrowRightIcon size={16} data-id="element-574" />
                  </button>
                </td>
              </tr>) : <tr data-id="element-575">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-576">
                No campaigns found
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}